import { useState, type JSX } from 'react'
import { SEO } from '@/components/seo'
import { Button } from '@/components/ui'

// How it works steps
const steps = [
  {
    number: 1,
    title: 'Share your link',
    description:
      'Get your unique referral link from your dashboard and send it to advisers who might benefit from WealthSpace.',
    color: 'blue',
  },
  {
    number: 2,
    title: 'They try WealthSpace',
    description:
      'Your referral gets a 7-day free trial to explore all features and see how WealthSpace transforms their practice.',
    color: 'emerald',
  },
  {
    number: 3,
    title: 'They get a free month',
    description:
      'When they subscribe, they receive 1 free month as a thank you. It\'s a genuine benefit for them too.',
    color: 'amber',
  },
  {
    number: 4,
    title: 'You earn rewards',
    description:
      'Once they become a paying customer, you unlock rewards. 1 adviser licence = 1 referral towards your next tier.',
    color: 'purple',
  },
]

// Physical rewards tiers
const physicalRewards = [
  {
    tier: 'Starter',
    referrals: 1,
    name: 'Voucher',
    value: '£100',
    color: 'emerald',
    featured: false,
  },
  {
    tier: 'Connector',
    referrals: 3,
    name: 'Fortnum & Mason Hamper',
    value: '£150',
    color: 'blue',
    featured: false,
  },
  {
    tier: 'Advocate',
    referrals: 5,
    name: 'Apple Watch',
    value: '£400',
    color: 'purple',
    featured: true,
  },
  {
    tier: 'Champion',
    referrals: 10,
    name: 'Voucher',
    value: '£1,000',
    color: 'amber',
    featured: false,
  },
  {
    tier: 'Legend',
    referrals: 25,
    name: 'Cash',
    value: '£2,500',
    color: 'rose',
    featured: false,
  },
]

// Cash payouts
const cashPayouts = [
  { referrals: 5, label: 'Payout #1', amount: '£650' },
  { referrals: 10, label: 'Payout #2', amount: '£1,000' },
  { referrals: 25, label: 'Final Payout', amount: '£2,500' },
]

// Dashboard features
const dashboardFeatures = [
  {
    title: 'Unique referral link',
    description:
      'Get your personalised referral link to share via email, LinkedIn, or directly with colleagues. Track every click.',
    icon: 'link',
  },
  {
    title: 'Track referrals',
    description:
      "See who's signed up, who's in trial, and who's converted. Know exactly where each referral stands.",
    icon: 'users',
  },
  {
    title: 'Reward progress',
    description:
      'See your current tier, progress to the next reward, and total value earned. Claim rewards directly from your dashboard.',
    icon: 'medal',
  },
]

// Programme terms
const terms = [
  {
    text: (
      <>
        <strong>Anyone can refer</strong> - you don't need to be a WealthSpace customer to
        participate in the partner programme
      </>
    ),
  },
  {
    text: (
      <>
        Referred advisers must be <strong>new to WealthSpace</strong> and not have previously held
        an account
      </>
    ),
  },
  { text: 'Each adviser can only be referred once - duplicate referrals will not be credited' },
  { text: 'Referral rewards unlock once the referred organisation becomes a paying customer' },
  {
    text: (
      <>
        <strong>1 adviser licence = 1 referral</strong> (an org with 5 advisers = 5 referrals)
      </>
    ),
  },
  {
    text: (
      <>
        <strong>Choose your track when you start:</strong> Physical Rewards or Cash is King - you
        cannot switch later
      </>
    ),
  },
  { text: 'Cash is King payouts occur at 5, 10, and 25 referrals (total £4,150)' },
  { text: 'Rewards are processed within 30 days of qualifying' },
  { text: 'WealthSpace reserves the right to modify or discontinue the programme at any time' },
]

// Step number color classes
const stepColors: Record<string, string> = {
  blue: 'bg-gradient-to-br from-[#3b82f6] to-[#2563eb]',
  emerald: 'bg-gradient-to-br from-[#10b981] to-[#059669]',
  amber: 'bg-gradient-to-br from-[#f59e0b] to-[#d97706]',
  purple: 'bg-gradient-to-br from-[#8b5cf6] to-[#7c3aed]',
}

// Reward icon background colors
const rewardIconColors: Record<string, string> = {
  emerald: 'bg-gradient-to-br from-[rgba(16,185,129,0.2)] to-[rgba(16,185,129,0.1)]',
  blue: 'bg-gradient-to-br from-[rgba(59,130,246,0.2)] to-[rgba(59,130,246,0.1)]',
  purple: 'bg-gradient-to-br from-[rgba(139,92,246,0.2)] to-[rgba(139,92,246,0.1)]',
  amber: 'bg-gradient-to-br from-[rgba(245,158,11,0.2)] to-[rgba(245,158,11,0.1)]',
  rose: 'bg-gradient-to-br from-[rgba(244,63,94,0.2)] to-[rgba(244,63,94,0.1)]',
}

const rewardTierColors: Record<string, string> = {
  emerald: 'text-[#10b981]',
  blue: 'text-[#3b82f6]',
  purple: 'text-[#8b5cf6]',
  amber: 'text-[#f59e0b]',
  rose: 'text-[#f43f5e]',
}

// Icon components
function RewardIcon({ color }: { color: string }) {
  const strokeColor: Record<string, string> = {
    emerald: '#10b981',
    blue: '#3b82f6',
    purple: '#8b5cf6',
    amber: '#f59e0b',
    rose: '#f43f5e',
  }

  // Different icons for each tier
  const icons: Record<string, JSX.Element> = {
    emerald: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={strokeColor[color]} strokeWidth="2">
        <path d="M20 12v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6" />
        <path d="M12 2L12 12" />
        <path d="M17 7l-5 5-5-5" />
      </svg>
    ),
    blue: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={strokeColor[color]} strokeWidth="2">
        <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <path d="M16 10a4 4 0 01-8 0" />
      </svg>
    ),
    purple: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={strokeColor[color]} strokeWidth="2">
        <circle cx="12" cy="12" r="7" />
        <polyline points="12 9 12 12 13.5 13.5" />
        <path d="M16.51 17.35l-.35 3.83a2 2 0 01-2 1.82H9.83a2 2 0 01-2-1.82l-.35-3.83m.01-10.7l.35-3.83A2 2 0 019.83 1h4.35a2 2 0 012 1.82l.35 3.83" />
      </svg>
    ),
    amber: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={strokeColor[color]} strokeWidth="2">
        <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14" />
      </svg>
    ),
    rose: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={strokeColor[color]} strokeWidth="2">
        <path d="M12 3l1.5 4.5h4.5l-3.5 3 1.5 4.5-4-3-4 3 1.5-4.5-3.5-3h4.5z" />
        <path d="M12 3l1 3M12 3l-1 3M19.5 9l-3 1M4.5 9l3 1M17 17l-2-2M7 17l2-2M12 21v-3" />
      </svg>
    ),
  }

  return icons[color] || icons.emerald
}

function DashboardIcon({ type }: { type: string }) {
  const props = { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: '#14b8a6', strokeWidth: 2 }

  switch (type) {
    case 'link':
      return (
        <svg {...props}>
          <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
        </svg>
      )
    case 'users':
      return (
        <svg {...props}>
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 00-3-3.87" />
          <path d="M16 3.13a4 4 0 010 7.75" />
        </svg>
      )
    case 'medal':
      return (
        <svg {...props}>
          <circle cx="12" cy="8" r="7" />
          <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
        </svg>
      )
    default:
      return null
  }
}

export default function PartnerProgrammePage() {
  const [activeTrack, setActiveTrack] = useState<'physical' | 'cash'>('physical')

  return (
    <>
      <SEO
        title="Partner Programme - WealthSpace | Refer Advisers, Earn Rewards"
        description="Join the WealthSpace Partner Programme. Refer fellow advisers and earn valuable rewards - from vouchers to an Apple Watch, or choose the Cash is King track."
        keywords="WealthSpace partner programme, referral rewards, financial adviser referrals, adviser software referral, earn rewards UK, partner dashboard"
        url="/partner-programme"
      />

      {/* Hero Section */}
      <section className="pt-[100px] pb-20 px-6 max-w-[1200px] mx-auto relative z-[1] text-center">
        <p className="text-sm font-semibold text-[#8b5cf6] tracking-[0.1em] uppercase mb-6">
          Partner Programme
        </p>
        <h1 className="font-serif text-[36px] sm:text-[clamp(42px,6vw,60px)] font-normal leading-[1.15] tracking-[-0.02em] mb-6 max-w-[900px] mx-auto">
          Refer advisers. <span className="text-[#8b5cf6]">Earn rewards.</span>
        </h1>
        <p className="text-[19px] text-brand-muted leading-[1.7] font-normal max-w-[600px] mx-auto mb-10">
          Share WealthSpace with fellow advisers and unlock valuable rewards - from vouchers to an
          Apple Watch. They get a free month too.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button href="https://wealthspace-web.vercel.app/settings/referrals" external>
            Access Partner Dashboard
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Button>
          <a
            href="#anyone-can-refer"
            className="px-9 py-[18px] rounded-md font-semibold text-base inline-flex items-center justify-center gap-2 transition-all duration-300 bg-white/5 text-brand-light border border-white/15 hover:bg-white/10 hover:border-white/25"
          >
            Not an adviser?
          </a>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-[100px] px-6 relative z-[1]">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl font-normal text-brand-light text-center mb-4">
            How it works
          </h2>
          <p className="text-lg text-brand-muted text-center max-w-[600px] mx-auto mb-10 sm:mb-[60px]">
            Four simple steps to start earning rewards
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step) => (
              <div
                key={step.number}
                className="text-center p-8 bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.06] rounded-2xl transition-all duration-300 hover:border-[rgba(139,92,246,0.3)] hover:-translate-y-1"
              >
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-5 text-xl font-bold text-white ${stepColors[step.color]}`}
                >
                  {step.number}
                </div>
                <h3 className="text-lg font-semibold text-brand-light mb-3">{step.title}</h3>
                <p className="text-sm text-brand-muted leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rewards Section */}
      <section className="py-16 sm:py-[100px] px-6 relative z-[1] bg-gradient-to-br from-[rgba(139,92,246,0.05)] to-transparent border-t border-b border-[rgba(139,92,246,0.1)]">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl font-normal text-brand-light text-center mb-4">
            Choose your reward track
          </h2>
          <p className="text-lg text-brand-muted text-center max-w-[600px] mx-auto mb-12">
            Pick between Physical Rewards or Cash is King when you start - your choice is permanent
          </p>

          {/* Track Selector */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <button
              onClick={() => setActiveTrack('physical')}
              className={`px-8 py-4 rounded-xl text-base font-semibold cursor-pointer transition-all duration-300 border-2 inline-flex items-center gap-2 ${
                activeTrack === 'physical'
                  ? 'bg-gradient-to-br from-[#8b5cf6] to-[#7c3aed] text-white border-[#8b5cf6]'
                  : 'bg-gradient-to-br from-[rgba(139,92,246,0.2)] to-[rgba(139,92,246,0.1)] text-[#a78bfa] border-[rgba(139,92,246,0.3)]'
              }`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 12v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6" />
                <path d="M12 2L12 12" />
                <path d="M17 7l-5 5-5-5" />
              </svg>
              Physical Rewards
            </button>
            <button
              onClick={() => setActiveTrack('cash')}
              className={`px-8 py-4 rounded-xl text-base font-semibold cursor-pointer transition-all duration-300 border-2 inline-flex items-center gap-2 ${
                activeTrack === 'cash'
                  ? 'bg-gradient-to-br from-[#f59e0b] to-[#d97706] text-brand-dark border-[#f59e0b]'
                  : 'bg-gradient-to-br from-[rgba(245,158,11,0.2)] to-[rgba(245,158,11,0.1)] text-[#fbbf24] border-[rgba(245,158,11,0.3)]'
              }`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="9" />
              </svg>
              Cash is King
            </button>
          </div>

          <div className="text-center mb-10">
            <p className="text-sm text-brand-subtle py-3 px-6 bg-white/[0.03] rounded-lg inline-block">
              Choose at signup - you cannot switch or mix tracks later
            </p>
          </div>

          {/* Physical Rewards Track */}
          {activeTrack === 'physical' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
              {physicalRewards.map((reward) => (
                <div
                  key={reward.tier}
                  className={`relative bg-gradient-to-br from-white/[0.04] to-white/[0.01] border rounded-2xl p-7 text-center transition-all duration-300 hover:border-[rgba(139,92,246,0.3)] hover:-translate-y-1 ${
                    reward.featured
                      ? 'border-[rgba(139,92,246,0.4)] bg-gradient-to-br from-[rgba(139,92,246,0.1)] to-[rgba(139,92,246,0.02)]'
                      : 'border-white/[0.08]'
                  }`}
                >
                  {reward.featured && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-gradient-to-br from-[#8b5cf6] to-[#7c3aed] text-white text-[11px] font-semibold py-1 px-3 rounded-full uppercase tracking-wide">
                      Popular
                    </span>
                  )}
                  <div className={`w-14 h-14 rounded-[14px] flex items-center justify-center mx-auto mb-4 ${rewardIconColors[reward.color]}`}>
                    <RewardIcon color={reward.color} />
                  </div>
                  <p className={`text-sm font-semibold mb-1 ${rewardTierColors[reward.color]}`}>
                    {reward.tier}
                  </p>
                  <p className="text-xs text-brand-subtle mb-4">
                    {reward.referrals} referral{reward.referrals !== 1 ? 's' : ''}
                  </p>
                  <p className="text-base font-semibold text-brand-light mb-2">{reward.name}</p>
                  <p className="text-2xl font-bold text-accent-teal">{reward.value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Cash Track */}
          {activeTrack === 'cash' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-[900px] mx-auto">
                {cashPayouts.map((payout) => (
                  <div
                    key={payout.referrals}
                    className="bg-gradient-to-br from-[rgba(245,158,11,0.1)] to-[rgba(245,158,11,0.02)] border border-[rgba(245,158,11,0.2)] rounded-2xl p-8 text-center"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[rgba(245,158,11,0.2)] flex items-center justify-center mx-auto mb-4 text-lg font-bold text-[#f59e0b]">
                      {payout.referrals}
                    </div>
                    <p className="text-sm text-brand-muted mb-2">{payout.label}</p>
                    <p className="text-[32px] font-bold text-[#f59e0b]">{payout.amount}</p>
                  </div>
                ))}
              </div>
              <div className="mt-10 p-6 bg-gradient-to-br from-[rgba(245,158,11,0.15)] to-[rgba(245,158,11,0.05)] border border-[rgba(245,158,11,0.3)] rounded-2xl text-center max-w-[400px] mx-auto">
                <p className="text-sm text-brand-muted mb-2">Total Potential Earnings</p>
                <p className="text-[36px] sm:text-[48px] font-bold text-[#f59e0b]">£4,150</p>
              </div>
            </>
          )}
        </div>
      </section>

      {/* What Your Referrals Get */}
      <section className="py-16 sm:py-[100px] px-6 relative z-[1]">
        <div className="max-w-[1200px] mx-auto">
          <div className="bg-gradient-to-br from-[rgba(20,184,166,0.1)] to-[rgba(20,184,166,0.02)] border border-[rgba(20,184,166,0.2)] rounded-[20px] p-8 sm:p-12 max-w-[800px] mx-auto text-center">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-accent-teal to-emerald-500 flex items-center justify-center mx-auto mb-6">
              <svg className="w-7 h-7 sm:w-8 sm:h-8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M20 12v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6" />
                <path d="M12 2L12 12" />
                <path d="M17 7l-5 5-5-5" />
              </svg>
            </div>
            <h3 className="font-serif text-[24px] sm:text-[28px] text-brand-light mb-4">What your referrals get</h3>
            <p className="text-lg text-brand-muted leading-[1.7]">
              Everyone you refer receives a{' '}
              <span className="text-accent-teal font-semibold">7-day free trial</span> to explore
              WealthSpace, plus{' '}
              <span className="text-accent-teal font-semibold">1 free month</span> when they
              subscribe. It's a genuine win-win - you're helping fellow advisers discover a tool
              that will transform their practice.
            </p>
          </div>
        </div>
      </section>

      {/* Partner Dashboard */}
      <section className="py-16 sm:py-[100px] px-6 relative z-[1] bg-gradient-to-br from-[rgba(20,184,166,0.03)] to-transparent">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl font-normal text-brand-light text-center mb-4">
            Your partner dashboard
          </h2>
          <p className="text-lg text-brand-muted text-center max-w-[600px] mx-auto mb-10 sm:mb-[60px]">
            Track your referrals and rewards in real-time
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {dashboardFeatures.map((feature) => (
              <div
                key={feature.title}
                className="p-8 bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.06] rounded-2xl transition-all duration-300 hover:border-[rgba(20,184,166,0.3)] hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[rgba(20,184,166,0.2)] to-[rgba(20,184,166,0.1)] flex items-center justify-center mb-5">
                  <DashboardIcon type={feature.icon} />
                </div>
                <h3 className="text-lg font-semibold text-brand-light mb-3">{feature.title}</h3>
                <p className="text-sm text-brand-muted leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Not an Adviser Section */}
      <section className="py-16 sm:py-[100px] px-6 relative z-[1]" id="anyone-can-refer">
        <div className="max-w-[1200px] mx-auto">
          <div className="bg-gradient-to-br from-[rgba(139,92,246,0.08)] to-[rgba(139,92,246,0.02)] border border-[rgba(139,92,246,0.2)] rounded-3xl py-10 sm:py-[60px] px-6 sm:px-12 text-center max-w-[800px] mx-auto">
            <div className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-2xl sm:rounded-[20px] bg-gradient-to-br from-[#8b5cf6] to-[#7c3aed] flex items-center justify-center mx-auto mb-6 sm:mb-7">
              <svg className="w-7 h-7 sm:w-8 sm:h-8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 00-3-3.87" />
                <path d="M16 3.13a4 4 0 010 7.75" />
              </svg>
            </div>
            <h2 className="font-serif text-[26px] sm:text-[32px] font-normal text-brand-light mb-4">
              Not an adviser? You can still refer!
            </h2>
            <p className="text-lg text-brand-muted leading-[1.7] max-w-[600px] mx-auto mb-8">
              Know a financial adviser who deserves better software?{' '}
              <span className="text-[#a78bfa] font-semibold">Anyone can refer advisers</span> and
              earn rewards. Whether you're an accountant, solicitor, mortgage broker, paraplanner,
              or simply a friend of an adviser - we'd love your help spreading the word.
            </p>

            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-10">
              {[
                'Same rewards as adviser partners',
                'Your referrals get a free month too',
                'Simple dashboard to track everything',
              ].map((benefit) => (
                <div
                  key={benefit}
                  className="flex items-center gap-2.5 py-3 px-5 bg-[rgba(139,92,246,0.1)] border border-[rgba(139,92,246,0.2)] rounded-xl text-sm text-brand-light font-medium"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <span>{benefit}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col items-center gap-4">
              <div className="inline-flex items-center gap-2.5 bg-gradient-to-br from-[rgba(139,92,246,0.15)] to-[rgba(139,92,246,0.05)] border border-[rgba(139,92,246,0.3)] py-4 px-7 rounded-xl">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span className="font-semibold text-[#a78bfa] text-base">
                  Partner Portal Coming Soon
                </span>
              </div>
              <p className="text-sm text-brand-subtle">
                We're building a dedicated portal for non-adviser partners. In the meantime, email{' '}
                <a href="mailto:partners@wealthspace.co.uk" className="text-[#8b5cf6] hover:underline">
                  partners@wealthspace.co.uk
                </a>{' '}
                to register your interest.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Terms Section */}
      <section className="py-16 sm:py-[100px] px-6 relative z-[1] bg-black/20">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl font-normal text-brand-light text-center mb-4">
            Programme terms
          </h2>
          <p className="text-lg text-brand-muted text-center max-w-[600px] mx-auto mb-10 sm:mb-[60px]">
            The important stuff, kept simple
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-[900px] mx-auto">
            {terms.map((term, index) => (
              <div key={index} className="flex gap-3 p-4 bg-white/[0.02] rounded-lg">
                <div className="w-1.5 h-1.5 rounded-full bg-accent-teal flex-shrink-0 mt-2" />
                <p className="text-sm text-brand-muted leading-relaxed">{term.text}</p>
              </div>
            ))}
          </div>

          <p className="text-center text-[13px] text-brand-subtle mt-10 max-w-[600px] mx-auto">
            Referral rewards are for platform usage only and have no bearing on financial advice.
            All referred parties are informed of this referral arrangement upon signup.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-[120px] px-6 max-w-[900px] mx-auto relative z-[1]">
        <div className="bg-gradient-to-br from-[rgba(139,92,246,0.1)] to-[rgba(139,92,246,0.02)] border border-[rgba(139,92,246,0.2)] rounded-3xl py-12 sm:py-20 px-8 sm:px-12 text-center">
          <h2 className="font-serif text-[32px] sm:text-[44px] font-normal text-brand-light tracking-[-0.02em] mb-5">
            Ready to start earning?
          </h2>
          <p className="text-lg text-brand-muted leading-[1.7] max-w-[500px] mx-auto mb-10">
            Access your partner dashboard to get your unique referral link and start sharing with
            fellow advisers today.
          </p>
          <Button href="https://wealthspace-web.vercel.app/settings/referrals" external size="lg">
            Go to Partner Dashboard
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Button>
          <p className="text-sm text-brand-subtle mt-6">Available to all WealthSpace customers</p>
        </div>
      </section>
    </>
  )
}
